import { useEffect, useRef, useState } from 'react';

import { useCancelToken } from '@/hooks/useCancelToken';
import { bffApiErrorHandler } from '@/shared/errorHandler';
import { BffApiServiceI } from '@/shared/bffApiService';
import { InferMethodArgumentPropertyType } from '@/models/MethodObjectArgPropType.model';
import { BffApiError, FrontendErrorCode } from '@/models/bff-api-error';
import { isBffResponse } from '@/utils/type-guards';

export type BffApiRequest<T> = <M extends BffApiServiceI[keyof BffApiServiceI]>(arg: {
  apiMethod: M;
  payload?: InferMethodArgumentPropertyType<M, 'payload'>;
  pathParams?: InferMethodArgumentPropertyType<M, 'pathParams'>;
  axiosConfig?: InferMethodArgumentPropertyType<M, 'config'>;
  successCallback?: (data: T) => void;
  errorCallback?: (error: BffApiError, fullError: unknown) => void;
  pollingInterval?: number; // in milliseconds
}) => void;

interface Return<T> {
  loading: boolean;
  success: boolean;
  error: BffApiError | null;
  data: T | null;
  request: BffApiRequest<T>;
  stopPolling: () => void;
}

export function useBffApiRequest<T>(): Return<T> {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<BffApiError | null>(null);
  const { newCancelToken } = useCancelToken();

  const pollingTimerRef = useRef<NodeJS.Timeout | null>(null);

  const stopPolling = () => {
    if (pollingTimerRef.current) {
      clearInterval(pollingTimerRef.current);
      pollingTimerRef.current = null;
    }
  };

  useEffect(() => {
    return () => {
      stopPolling(); // cleanup on unmount
    };
  }, []);

  const request: BffApiRequest<T> = arg => {
    const {
      apiMethod,
      payload,
      pathParams,
      axiosConfig,
      successCallback,
      errorCallback,
      pollingInterval,
    } = arg;

    const config = {
      ...axiosConfig,
      cancelToken: newCancelToken(),
    };

    const executeRequest = () => {
      setLoading(true);
      setSuccess(false);
      setError(null);

      // @ts-ignore
      apiMethod({ payload, pathParams, config })
        .then(response => {
          setLoading(false);
          if (response.status === 204) {
            setSuccess(true);
            setData(null);
            // @ts-ignore
            successCallback?.(null);
            return;
          }

          if (isBffResponse(response.data)) {
            setSuccess(true);
            // @ts-ignore
            setData(response.data.data);
            // @ts-ignore
            successCallback?.(response.data.data);
          } else {
            const unexpectedFormatError = { code: FrontendErrorCode.UnexpectedResponseFormat };
            setSuccess(false);
            setError(unexpectedFormatError);
            errorCallback?.(unexpectedFormatError, null);
          }
        })
        .catch(error => {
          setLoading(false);
          setSuccess(false);
          const handledError = bffApiErrorHandler(error);
          setError(handledError);
          errorCallback?.(handledError, error);
        });
    };

    executeRequest();

    stopPolling();
    if (pollingInterval && pollingInterval > 0) {
      pollingTimerRef.current = setInterval(executeRequest, pollingInterval);
    }
  };

  return {
    loading,
    success,
    error,
    data,
    request,
    stopPolling,
  };
}
